import React, { useState } from 'react';
import { Box, Button, FormControlLabel, Paper, Switch, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatValuta } from '@gestionale/core/i18n/formattazione';
import type { ContestoLocale } from '@gestionale/core/i18n/lingue';
import { VIOLA_NOTTE } from '@shared/yuma-sito/gatto';
import type { SpesaDiYuma } from '@shared/yuma-sito/tipi';
import { tokens } from '../../theme';
import { impostaYumaSito } from '../../services/yumaSitoService';

/**
 * La spesa di Yuma sul sito e le sue due leve (30/10/2026): l'interruttore (Yuma risponde o no) e il tetto del giorno,
 * che Simone alza quando arrivano richieste vere. Oggi contro il tetto è il numero che conta, con la barra che si
 * riempie; sotto, gli ultimi 30 giorni. Ogni modifica la salva e la rilegge il server.
 */
export const RiquadroSpesa: React.FC<{ spesa: SpesaDiYuma; ctx: ContestoLocale; onCambiata: (s: SpesaDiYuma) => void }> = ({ spesa, ctx, onCambiata }) => {
  const { t } = useTranslation('yumaSito');
  const [modifica, setModifica] = useState<string | null>(null);
  const [salvataggio, setSalvataggio] = useState(false);
  const [esito, setEsito] = useState<{ testo: string; errore: boolean } | null>(null);
  const usd = (v: number) => formatValuta(v, ctx, { valuta: 'USD' });

  const salva = async (modifiche: { tetto_giornaliero_usd?: number; attiva?: boolean }) => {
    setSalvataggio(true);
    setEsito(null);
    try {
      onCambiata(await impostaYumaSito(modifiche));
      setModifica(null);
      setEsito({ testo: t('impostazioni.salvato'), errore: false });
    } catch (e) {
      setEsito({ testo: t('impostazioni.errore', { motivo: e instanceof Error ? e.message : String(e) }), errore: true });
    } finally {
      setSalvataggio(false);
    }
  };

  const nuovoTetto = modifica === null ? NaN : Number(modifica.replace(',', '.'));
  const pieno = spesa.tetto_giornaliero_usd ? Math.min(100, (spesa.oggi_usd / spesa.tetto_giornaliero_usd) * 100) : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 4 }, alignItems: 'center' }}>
        <FormControlLabel
          control={<Switch checked={spesa.attiva} disabled={salvataggio} onChange={(e) => void salva({ attiva: e.target.checked })} />}
          label={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{t('impostazioni.interruttore')}</Typography>}
        />
        <Box sx={{ minWidth: 240, flex: { md: '0 1 340px' } }}>
          <Typography variant="body2" color="text.secondary">{t('spesa.oggi')}</Typography>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums', color: tokens.color.graphite }}>
            {usd(spesa.oggi_usd)}
            <Box component="span" sx={{ ml: 1, fontSize: '0.85rem', fontWeight: 500, color: tokens.color.ash }}>
              {t('spesa.tetto', { tetto: usd(spesa.tetto_giornaliero_usd) })}
            </Box>
          </Typography>
          <Box sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: tokens.color.lineSoft, overflow: 'hidden' }} aria-hidden>
            <Box sx={{ height: '100%', width: `${pieno}%`, bgcolor: pieno >= 100 ? tokens.color.danger : VIOLA_NOTTE }} />
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">{t('spesa.trenta')}</Typography>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums', color: tokens.color.graphite }}>
            {usd(spesa.ultimi_30_giorni_usd)}
          </Typography>
        </Box>
        {modifica === null && (
          <Button variant="outlined" size="small" onClick={() => setModifica(String(spesa.tetto_giornaliero_usd))} sx={{ minHeight: 40 }}>
            {t('impostazioni.cambiaTetto')}
          </Button>
        )}
      </Box>

      {modifica !== null && (
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            if (Number.isFinite(nuovoTetto) && nuovoTetto > 0) void salva({ tetto_giornaliero_usd: nuovoTetto });
          }}
          sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}
        >
          <TextField
            size="small"
            label={t('impostazioni.nuovoTetto')}
            value={modifica}
            onChange={(e) => setModifica(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            sx={{ width: 220 }}
            autoFocus
          />
          <Button type="submit" variant="contained" disabled={salvataggio || !(Number.isFinite(nuovoTetto) && nuovoTetto > 0)} sx={{ minHeight: 40 }}>
            {t('impostazioni.salva')}
          </Button>
          <Button onClick={() => setModifica(null)} disabled={salvataggio} sx={{ minHeight: 40 }}>
            {t('impostazioni.annulla')}
          </Button>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
        {t('impostazioni.spiegazioneTetto')}
      </Typography>
      {esito && (
        <Typography role="status" sx={{ mt: 1, fontSize: '0.85rem', color: esito.errore ? tokens.color.danger : tokens.color.success }}>
          {esito.testo}
        </Typography>
      )}
    </Paper>
  );
};
